import React, { useContext, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { Link } from "@reach/router";
import { ArrowLeft, ArrowRight, Search } from "react-feather";
import Header from "../../../components/Header";
import { AuthContext } from "../../../services/user/AuthProvider";
import InviteSchoolAdministrator from "./InviteSchoolAdministrator";
import { GET_FRANCHISE_DETAILS, GET_FRANCHISE_SCHOOL_ADMINISTRATORS } from "../services/graphql";
import styles from "../Franchises.module.scss";

const FranchiseAdministrators = ({ franchiseId }) => {
  const { currentUser } = useContext(AuthContext);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ search: "", page: 1 });
  const [showInvite, setShowInvite] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  const franchiseQuery = useQuery(GET_FRANCHISE_DETAILS, {
    variables: { franchiseId },
    fetchPolicy: "cache-first"
  });
  const { data, loading, error, refetch } = useQuery(GET_FRANCHISE_SCHOOL_ADMINISTRATORS, {
    variables: { franchiseId, ...filters, perPage: 6 },
    fetchPolicy: "no-cache"
  });
  const franchise = franchiseQuery.data && franchiseQuery.data.franchise;
  const directory = data && data.franchiseSchoolAdministrators;
  const administrators = (directory && directory.administrators) || [];
  const currentPage = (directory && directory.currentPage) || 1;
  const totalPages = (directory && directory.totalPages) || 0;
  const pageNumbers = [];
  for (let page = Math.max(1, currentPage - 2); page <= Math.min(totalPages, currentPage + 2); page += 1) {
    pageNumbers.push(page);
  }
  const handleSearch = event => {
    event.preventDefault();
    setFilters({ search: search.trim(), page: 1 });
  };
  const changePage = page => setFilters({ ...filters, page });

  return (
    <div className={`${styles.franchises} ${styles.portal}`}>
      <Header user={currentUser} portalFranchise={franchise} portalFranchiseId={franchiseId} portalPage="admins" />
      <div className={`${styles.header} ${styles.detailHeader}`}>
        <div>
          <p className={styles.territoryLabel}>{franchise ? `${franchise.name} territory` : ""}</p>
          <h1 className="title">Administrator users</h1>
          <p>View school administrators across all {franchise ? `${franchise.name} ` : ""}franchise schools.</p>
        </div>
        <button type="button" className={styles.primaryButton} onClick={() => setShowInvite(true)} disabled={!franchise}>Invite admin</button>
      </div>
      {showInvite && <InviteSchoolAdministrator franchiseId={franchiseId} closeModal={() => setShowInvite(false)} onInvited={() => {
        setShowInvite(false);
        setRefreshError("");
        refetch().catch(() => setRefreshError("Invitation sent, but the administrator list could not be refreshed. Please reload the page."));
      }} />}
      {refreshError && <p role="alert">{refreshError}</p>}
      {franchiseQuery.error && <p role="alert">Unable to load franchise details. Please reload the page.</p>}
      {directory && <div className={`${styles.overview} ${styles.section}`}>
        <div className={styles.overviewCard}><h3>Total admins</h3><strong>{(directory.totalAdminsCount || 0).toLocaleString("en-GB")}</strong></div>
        <div className={styles.overviewCard}><h3>Active</h3><strong>{(directory.activeAdminsCount || 0).toLocaleString("en-GB")}</strong></div>
        <div className={styles.overviewCard}><h3>Invites pending</h3><strong>{(directory.pendingInvitesCount || 0).toLocaleString("en-GB")}</strong></div>
        <div className={styles.overviewCard}><h3>Schools without admin</h3><strong className={styles.noAdminCount}>{(directory.schoolsWithoutAdminCount || 0).toLocaleString("en-GB")}</strong></div>
      </div>}
      <form className={`${styles.schoolSearch} ${styles.adminDirectorySearch}`} onSubmit={handleSearch}>
        <div className={styles.searchInput}>
          <Search size={16} aria-hidden="true" />
          <input type="search" aria-label="Search name, email or school" placeholder="Search name, email or school" value={search} onChange={event => setSearch(event.target.value)} />
        </div>
        <button type="submit" className={styles.primaryButton} disabled={loading}>Search</button>
      </form>
      {loading && <p role="status">Loading administrators...</p>}
      {error && <p role="alert">Unable to load administrators. Please try again later.</p>}
      {!loading && !error && !directory && <p>Administrator directory not available.</p>}
      {!loading && !error && directory && <div className={styles.section}>
        <h2>All school administrators</h2>
        {administrators.length === 0 ? <p>No administrators found.</p> : <div className={styles.schoolsTableWrapper}>
          <table className={`${styles.schoolsTable} ${styles.directoryTable} ${styles.adminDirectoryTable}`}>
            <thead><tr>
              <th scope="col">Name</th><th scope="col">School</th><th scope="col">Email</th><th scope="col">Status</th><th scope="col">Last active</th>
              <th scope="col"><span className={styles.visuallyHidden}>Manage school administrators</span></th>
            </tr></thead>
            <tbody>{administrators.map(admin => {
              const path = `/franchise-portal/${franchiseId}/schools/${admin.schoolId}/admins`;
              return <tr key={`${admin.schoolId}-${admin.teacherId}`}>
                <th scope="row">{admin.name}</th>
                <td><Link to={path}>{admin.schoolName}</Link></td>
                <td>{admin.email}</td>
                <td><span className={styles.detailStatus} data-status={admin.statusLabel.toLowerCase()}>{admin.statusLabel}</span></td>
                <td>{admin.lastActiveFormatted || "—"}</td>
                <td><ArrowRight size={16} aria-hidden="true" /></td>
              </tr>;
            })}</tbody>
          </table>
        </div>}
        <div className={styles.directoryFooter}>
          <p>{directory.totalCount || 0} administrators</p>
          {totalPages > 1 && <nav className={styles.pagination} aria-label="Administrator pages">
            <button type="button" aria-label="Previous page" disabled={currentPage <= 1} onClick={() => changePage(currentPage - 1)}><ArrowLeft size={16} aria-hidden="true" /></button>
            {pageNumbers.map(page => <button type="button" key={page} aria-label={`Page ${page}`} aria-current={page === currentPage ? "page" : undefined} onClick={() => changePage(page)}>{page}</button>)}
            <button type="button" aria-label="Next page" disabled={currentPage >= totalPages} onClick={() => changePage(currentPage + 1)}><ArrowRight size={16} aria-hidden="true" /></button>
          </nav>}
        </div>
      </div>}
      <div className={styles.schoolFormNotice}>
        <h3>Administrator access remains school-scoped</h3>
        <p>Selecting a school opens its administrator management view.</p>
      </div>
    </div>
  );
};

export default FranchiseAdministrators;
